import type * as React from 'react';
import type { ViewProps, ViewStyle } from 'react-native';

import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
} from '@gitbook/doc-core';
import type { Block, Document, Fragment } from '@gitbook/slate';
import type { BlockProps } from '@gitbook/slate-react';
import tokens from '@gitbook/spine-tokens';

import { spacing } from '../../constants';
import { DOMView } from '../../DragAndDrop';
/**
 * A basic block wrapper that wraps the block node with appropriate spacing.
 */
export function BasicBlockWrapper(
    props: BlockProps & {
        /** HTML tag to use */
        tag?: keyof HTMLElementTagNameMap;
        /** Style props to pass the top-level content view */
        contentStyleProps?: Pick<ViewProps, 'style'>;
    }
): React.ReactElement {
    const { node, parent, children, attributes, tag, contentStyleProps = {} } = props;

    const blockSpacing = getBasicBlockSpacing(node, parent);

    return (
        <DOMView
            tag={tag}
            {...attributes}
            data-block-content={node.key}
            style={[blockSpacing.top, blockSpacing.bottom, contentStyleProps.style]}
        >
            {children}
        </DOMView>
    );
}

interface BasicBlockSpacing {
    top: ViewStyle;
    bottom: ViewStyle;
}

/**
 * Helper method to determine the spacing between block nodes.
 */
function getBasicBlockSpacing(node: Block, parent: Block | Document | Fragment): BasicBlockSpacing {
    const index = parent.getChildIndex(node.key);
    const isFirst = index === 0;

    let blockSpacing: BasicBlockSpacing | null = null;
    let parentOverride: BasicBlockSpacing | null = null;

    switch (node.type) {
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3:
            blockSpacing = { top: spacing.Margin.top600, bottom: spacing.Margin.bottom200 };
            break;
        case BLOCK_PARAGRAPH:
            blockSpacing = { top: spacing.Margin.top200, bottom: spacing.Margin.bottom200 };
            break;
        case BLOCK_LIST_OL:
        case BLOCK_LIST_UL:
        case BLOCK_LIST_TASKS:
            blockSpacing = { top: spacing.Margin.top400, bottom: spacing.Margin.bottom400 };
            break;
        case BLOCK_LIST_ITEM:
            blockSpacing = { top: spacing.Margin.top200, bottom: {} };
            break;
        default:
            blockSpacing = { top: spacing.Margin.top400, bottom: spacing.Margin.bottom400 };
    }

    switch (parent.type) {
        case BLOCK_LIST_ITEM: {
            parentOverride = { top: spacing.Margin.top200, bottom: {} };
            break;
        }
    }

    if (isFirst) {
        blockSpacing.top = {};
        if (parentOverride) {
            parentOverride.top = {};
        }
    }

    return {
        ...blockSpacing,
        ...parentOverride,
    };
}

/**
 * Helper method to determine what font size is currently being used in the block
 */
export function getBasicBlockFontSize(node: Block | null): number {
    const { typography } = tokens;
    if (!node) {
        return typography.content.paragraph['font-size'];
    }

    switch (node.type) {
        case BLOCK_HEADING_1:
            return typography.content.heading.large['font-size'];
        case BLOCK_HEADING_2:
            return typography.content.heading.medium['font-size'];
        case BLOCK_HEADING_3:
            return typography.content.heading.small['font-size'];
        default:
            return typography.content.paragraph['font-size'];
    }
}
